import React from 'react';
import { Gamepad2, Users, Trophy, Gift } from 'lucide-react';

export const HowItWorksSection = () => {
  const steps = [
    {
      number: '1',
      icon: Gamepad2,
      title: 'Escolha um modo',
      description: 'Treino, Casual ou Competitivo',
      color: 'green'
    },
    {
      number: '2',
      icon: Users,
      title: 'Entre na partida',
      description: 'Match em segundos',
      color: 'purple'
    },
    {
      number: '3',
      icon: Trophy,
      title: 'Vença',
      description: 'Suba no ranking',
      color: 'green'
    },
    {
      number: '4',
      icon: Gift,
      title: 'Conquiste',
      description: 'Recompensas e eventos',
      color: 'purple'
    }
  ];

  return (
    <section id="como-jogar" className="py-12 sm:py-16 lg:py-24 bg-[#0A0A0F] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-primary/20 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-12">
          <p className="overline mb-2">4 Passos</p>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black font-archive text-white">
            Como Jogar
          </h2>
        </div>

        {/* Mobile: Horizontal Carousel */}
        <div className="lg:hidden">
          <div className="snap-carousel hidden-scrollbar -mx-4">
            {steps.map((step) => {
              const Icon = step.icon;
              const isGreen = step.color === 'green';
              
              return (
                <div
                  key={step.number}
                  className="w-[280px] flex-shrink-0 first:ml-4 last:mr-4"
                >
                  <div className={`game-card p-5 rounded-2xl h-full ${isGreen ? 'border-green-primary/30' : 'border-purple-primary/30'}`}>
                    {/* Number Badge */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      isGreen 
                        ? 'bg-gradient-to-br from-green-primary to-green-secondary' 
                        : 'bg-gradient-to-br from-purple-primary to-purple-light'
                    }`}>
                      <span className="text-xl font-black text-white">{step.number}</span>
                    </div>
                    
                    {/* Icon */}
                    <Icon className={`mb-3 ${isGreen ? 'text-green-primary' : 'text-purple-light'}`} size={28} />
                    
                    {/* Content */}
                    <h3 className={`text-lg font-bold font-archive mb-1 ${isGreen ? 'text-green-primary' : 'text-purple-light'}`}>
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-400 font-hero">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Scroll hint */}
          <p className="text-center text-xs text-gray-500 mt-4">
            ← Deslize para ver mais →
          </p>
        </div>

        {/* Desktop: Grid */}
        <div className="hidden lg:grid grid-cols-4 gap-6 max-w-5xl mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isGreen = step.color === 'green';
            
            return (
              <div key={step.number} className="relative">
                <div className={`game-card p-6 rounded-2xl text-center ${isGreen ? 'border-green-primary/30' : 'border-purple-primary/30'}`}>
                  {/* Number Badge */}
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                    isGreen 
                      ? 'bg-gradient-to-br from-green-primary to-green-secondary' 
                      : 'bg-gradient-to-br from-purple-primary to-purple-light'
                  }`}>
                    <span className="text-2xl font-black text-white">{step.number}</span>
                  </div>
                  
                  {/* Icon */}
                  <Icon className={`mx-auto mb-3 ${isGreen ? 'text-green-primary' : 'text-purple-light'}`} size={32} />
                  
                  {/* Content */}
                  <h3 className={`text-xl font-bold font-archive mb-2 ${isGreen ? 'text-green-primary' : 'text-purple-light'}`}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-400 font-hero">
                    {step.description}
                  </p>
                </div>
                
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-purple-primary/50 to-green-primary/50"></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Game Screenshots - Horizontal scroll on mobile */}
        <div className="mt-10 lg:mt-16">
          <div className="snap-carousel hidden-scrollbar -mx-4 lg:mx-0 lg:flex lg:justify-center lg:gap-6">
            <div className="w-[300px] lg:w-[400px] flex-shrink-0 first:ml-4 lg:first:ml-0 last:mr-4 lg:last:mr-0">
              <div className="relative group rounded-2xl overflow-hidden border border-purple-primary/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_23cdd01a-1675-45c9-80c0-20919ae946f2/artifacts/yjrk6snj_image.png"
                  alt="Modos de jogo"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <p className="absolute bottom-3 left-3 text-white text-sm font-medium">Escolha seu modo</p>
              </div>
            </div>
            <div className="w-[300px] lg:w-[400px] flex-shrink-0 first:ml-4 lg:first:ml-0 last:mr-4 lg:last:mr-0">
              <div className="relative group rounded-2xl overflow-hidden border border-green-primary/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_23cdd01a-1675-45c9-80c0-20919ae946f2/artifacts/kqehw8cz_image.png"
                  alt="Vitória"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <p className="absolute bottom-3 left-3 text-white text-sm font-medium">Vença e conquiste!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
